import { Center, Flex, Progress, Stack, Text, useMantineTheme } from "@mantine/core";
import React from "react";
import { BackButton } from "./BackButton";
import { StyledActionButton } from "./StyledActionButton";

interface QuestionnaireUILayoutProps {
  progressValue: number;
  showBackButton: boolean;
  onBackButtonClick: () => void;
  title: string;
  subtitle?: string;
  questionAreaContent: React.ReactNode;
  showNextButton: boolean;
  onNextButtonClick: () => void;
  isNextButtonDisabled: boolean;
  isNextButtonLoading: boolean;
  nextButtonText?: string;
  showSubmitButton: boolean;
  onSubmitButtonClick: () => void;
  isSubmitButtonDisabled: boolean;
  submitButtonText?: string;
  isErrorState?: boolean;
  errorMessage?: string;
  pbForQuestionTitle?: string | number;
  isSubmitButtonLoading?: boolean;
  topActionButton?: React.ReactNode;
}

export function QuestionnaireUILayout({
  progressValue,
  showBackButton,
  onBackButtonClick,
  title,
  subtitle,
  questionAreaContent,
  showNextButton,
  onNextButtonClick,
  isNextButtonDisabled,
  isNextButtonLoading,
  nextButtonText = "다음",
  showSubmitButton,
  onSubmitButtonClick,
  isSubmitButtonDisabled,
  submitButtonText = "제출",
  isErrorState,
  errorMessage,
  pbForQuestionTitle = "xl",
  isSubmitButtonLoading,
  topActionButton,
}: QuestionnaireUILayoutProps) {
  const theme = useMantineTheme();

  return (
    <Stack justify="space-between" h="100%" pt="lg">
      {/* Top Bar: Back Button and Progress */}
      <Center w="100%" pos="relative" mih={50}>
        {showBackButton && <BackButton onClick={onBackButtonClick} />}
        <Progress w="75%" size="xl" value={progressValue} />
      </Center>

      {/* Question Display Area: Title, Subtitle, and actual Question Input Area */}
      <Center style={{ flexGrow: 1 }}>
        <Stack w="75%">
          {topActionButton && <Center mb="md">{topActionButton}</Center>}
          <Flex direction="column" align="center" pb={pbForQuestionTitle}>
            <Text size="2rem" c={theme.colors.yellow[0]} fw={700} ta="center" pb="sm" lh="1.3">
              {title}
            </Text>
            {subtitle && (
              <Text
                size="1rem"
                c={"white"}
                fw={700}
                ta="center"
                lh="1.4"
                style={{ whiteSpace: "pre-line" }}
              >
                {subtitle}
              </Text>
            )}
          </Flex>
          {questionAreaContent}
        </Stack>
      </Center>

      {/* Bottom Button Area: Next/Submit Buttons and Error Message */}
      <Center pb="xl">
        <Stack align="center" w="100%">
          {showNextButton && (
            <StyledActionButton
              onClick={onNextButtonClick}
              disabled={isNextButtonDisabled}
              loading={isNextButtonLoading}
            >
              {nextButtonText}
            </StyledActionButton>
          )}

          {isErrorState && errorMessage && <Text c="red">{errorMessage}</Text>}

          {showSubmitButton && (
            <StyledActionButton
              onClick={onSubmitButtonClick}
              disabled={isSubmitButtonDisabled}
              loading={isSubmitButtonLoading}
            >
              {submitButtonText}
            </StyledActionButton>
          )}
        </Stack>
      </Center>
    </Stack>
  );
}
